import { useState, useEffect, useRef } from 'react'
import CodeMirror from '@uiw/react-codemirror'
import { json } from '@codemirror/lang-json'
import { oneDark } from '@codemirror/theme-one-dark'
import { useThemeStore } from '@/store/themeStore'
import { AlertCircle } from 'lucide-react'
import type { TransactionModel } from './types'

interface JsonEditorPanelProps {
  model: TransactionModel
  onModelChange: (model: TransactionModel) => void
}

export default function JsonEditorPanel({ model, onModelChange }: JsonEditorPanelProps) {
  const { theme } = useThemeStore()
  const [editorValue, setEditorValue] = useState('')
  const [parseError, setParseError] = useState<string | null>(null)
  const modelVersionRef = useRef(0)

  // Sync editor content when model changes externally (GUI edits, undo/redo)
  useEffect(() => {
    modelVersionRef.current += 1
    setEditorValue(JSON.stringify(model, null, 2))
    setParseError(null)
  }, [model])

  const handleChange = (value: string) => {
    setEditorValue(value)
    // Validate on each keystroke to show errors, but don't apply
    try {
      JSON.parse(value)
      setParseError(null)
    } catch (e: unknown) {
      setParseError((e as Error).message)
    }
  }

  const handleApply = () => {
    try {
      const parsed = JSON.parse(editorValue) as TransactionModel
      if (!parsed.obj_name && !parsed.sql_models) {
        setParseError('Invalid transaction model: missing obj_name or sql_models')
        return
      }
      setParseError(null)
      onModelChange(parsed)
    } catch (e: unknown) {
      setParseError((e as Error).message)
    }
  }

  const handleFormat = () => {
    try {
      const parsed = JSON.parse(editorValue)
      setEditorValue(JSON.stringify(parsed, null, 2))
      setParseError(null)
    } catch {
      // Can't format invalid JSON — error is already shown
    }
  }

  const isModified = (() => {
    try {
      return JSON.stringify(JSON.parse(editorValue)) !== JSON.stringify(model)
    } catch {
      return true
    }
  })()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '6px 12px',
        borderBottom: '1px solid var(--color-border)',
        flexShrink: 0,
      }}>
        <span style={{ fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>
          Transaction Model JSON
        </span>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button className="btn btn-ghost btn-sm" onClick={handleFormat}>
            Format
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleApply}
            disabled={!!parseError || !isModified}
          >
            Apply
          </button>
        </div>
      </div>

      {/* Parse error banner */}
      {parseError && (
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 6,
          padding: '6px 12px',
          background: 'rgba(239, 83, 80, 0.1)',
          borderBottom: '1px solid rgba(239, 83, 80, 0.3)',
          color: 'var(--color-error, #cf222e)',
          fontSize: '0.8125rem',
          lineHeight: 1.4,
          flexShrink: 0,
        }}>
          <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span style={{ wordBreak: 'break-word' }}>{parseError}</span>
        </div>
      )}

      {/* Editor */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <CodeMirror
          value={editorValue}
          onChange={handleChange}
          extensions={[json()]}
          theme={theme === 'dark' ? oneDark : undefined}
          height="100%"
          style={{ height: '100%' }}
          basicSetup={{
            lineNumbers: true,
            foldGutter: true,
            highlightActiveLine: true,
            autocompletion: true,
          }}
        />
      </div>
    </div>
  )
}
